import { X } from 'lucide-react';
import { VARIABLES } from '@app/components/common/constants';
import { ForecastModel, BaseForecastModelWithAvailability } from '@app/common/static-data/models';
import { useState } from 'react';

import { ResizeHandle } from './ResizeHandle';
import { useForecastResize } from '../hooks/useForecastResize';
import { VariableSection } from './VariableSection';
import { ModelSection } from './ModelSection';
import { TimeRangeSection } from './TimeRangeSection';
import { LocationSection } from './LocationSection';
import { CustomCoordinateInput } from './CustomCoordinateInput';

type VariableId = (typeof VARIABLES)[number]['id'];

interface Location {
  lat: number;
  lon: number;
  name: string;
}

interface ForecastPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onWidthChange?: (width: number) => void;
  onResizeChange?: (isResizing: boolean) => void;
  // Control props
  selectedParam: VariableId;
  onParamChange: (param: VariableId) => void;
  selectedModels: ForecastModel[];
  onModelsChange: (models: ForecastModel[]) => void;
  endHour: number;
  onEndHourChange: (hour: number) => void;
  availableModels: BaseForecastModelWithAvailability[];
  loading: boolean;
  // Location props
  location: Location;
  onLocationChange: (location: Location) => void;
  favoriteLocations: Location[];
  addFavoriteLocation: (location: Location) => void;
  removeFavoriteLocation: (locationName: string) => void;
  isFavoriteLocation: (locationName: string) => boolean;
  // Shared favorites state
  favoriteVariables: Set<string>;
  favoriteModels: Set<string>;
  onFavoriteVariablesChange: (favorites: Set<string>) => void;
  onFavoriteModelsChange: (favorites: Set<string>) => void;
  // Model colors
  customModelColors: Record<string, string>;
  onModelColorChange: (modelId: string, color: string) => void;
  // Panel state
  expandedVariableSection?: boolean;
  onExpandedVariableSectionChange?: (expanded: boolean) => void;
  expandedLocationSection?: boolean;
  onExpandedLocationSectionChange?: (expanded: boolean) => void;
  expandedModelSection?: boolean;
  onExpandedModelSectionChange?: (expanded: boolean) => void;
  expandedTimeRangeSection?: boolean;
  onExpandedTimeRangeSectionChange?: (expanded: boolean) => void;
  // Init times
  availableInitTimes?: Record<string, string[]>;
  loadingInitTimes?: Record<string, boolean>;
  openInitTimeSelectors?: Record<string, boolean>;
  selectedInitTimes?: Record<string, string>;
  onInitTimeChange?: (modelId: string, initTime: string) => void;
  onInitTimeSelectorToggle?: (modelId: string) => void;
}

export function ForecastPanel({
  isOpen,
  onClose,
  onWidthChange,
  onResizeChange,
  selectedParam,
  onParamChange,
  selectedModels,
  onModelsChange,
  endHour,
  onEndHourChange,
  availableModels,
  loading,
  location,
  onLocationChange,
  favoriteLocations,
  addFavoriteLocation,
  removeFavoriteLocation,
  isFavoriteLocation,
  favoriteVariables,
  favoriteModels,
  onFavoriteVariablesChange,
  onFavoriteModelsChange,
  customModelColors,
  onModelColorChange,
  expandedVariableSection = false,
  onExpandedVariableSectionChange,
  expandedLocationSection = false,
  onExpandedLocationSectionChange,
  expandedModelSection = false,
  onExpandedModelSectionChange,
  expandedTimeRangeSection = false,
  onExpandedTimeRangeSectionChange,
  availableInitTimes = {},
  loadingInitTimes = {},
  openInitTimeSelectors = {},
  selectedInitTimes = {},
  onInitTimeChange,
  onInitTimeSelectorToggle,
}: ForecastPanelProps) {
  const { handleResizeStart } = useForecastResize({ onWidthChange, onResizeChange });
  const [showCustomCoordinates, setShowCustomCoordinates] = useState(false);
  const [pendingCustomLocation, setPendingCustomLocation] = useState<Location | null>(null);

  const toggleVariableFavorite = (varId: string) => {
    const newSet = new Set(favoriteVariables);
    if (newSet.has(varId)) {
      newSet.delete(varId);
    } else {
      newSet.add(varId);
    }
    onFavoriteVariablesChange(newSet);
  };

  const toggleModelFavorite = (modelId: string) => {
    const newSet = new Set(favoriteModels);
    if (newSet.has(modelId)) {
      newSet.delete(modelId);
    } else {
      newSet.add(modelId);
    }
    onFavoriteModelsChange(newSet);
  };

  return (
    <div
      className={`
        w-full h-full bg-theme-light-bg dark:bg-theme-dark-bg relative
        ${onWidthChange ? 'transform transition-transform duration-300 ease-in-out' : ''}
        ${onWidthChange && isOpen ? 'translate-x-0' : ''}
        ${onWidthChange && !isOpen ? 'translate-x-full' : ''}
      `}
      style={{
        // Only allow overflow on desktop for color picker
        overflow: onWidthChange ? 'visible' : 'hidden',
      }}
    >
      {/* Resize handle - only show on desktop */}
      <ResizeHandle onResizeStart={handleResizeStart} show={!!onWidthChange} />

      {/* Panel Content */}
      <div
        className="p-4 h-full flex flex-col"
        style={{ overflow: onWidthChange ? 'visible' : 'hidden' }}
      >
        <div
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm h-full flex flex-col relative"
          style={{ overflow: onWidthChange ? 'visible' : 'hidden' }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Forecast Settings
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Controls Section - Scrollable but allow color picker overflow */}
          <div
            className="flex-1 overflow-y-auto"
            style={{
              // Allow horizontal overflow for color picker on desktop only
              overflowX: onWidthChange ? 'visible' : 'hidden',
              // Ensure proper scrolling on mobile
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {/* Location Section */}
              <div className="p-4 location-section-button">
                <LocationSection
                  location={location}
                  onLocationChange={onLocationChange}
                  favoriteLocations={favoriteLocations}
                  addFavoriteLocation={addFavoriteLocation}
                  removeFavoriteLocation={removeFavoriteLocation}
                  isFavoriteLocation={isFavoriteLocation}
                  loading={loading}
                  expanded={expandedLocationSection}
                  onExpandedChange={onExpandedLocationSectionChange}
                  showCustomCoordinates={showCustomCoordinates}
                  onShowCustomCoordinatesChange={setShowCustomCoordinates}
                  pendingCustomLocation={pendingCustomLocation}
                  onPendingCustomLocationHandled={() => setPendingCustomLocation(null)}
                />
              </div>

              {/* Variables Section */}
              <div className="p-4 variables-section-button">
                <VariableSection
                  selectedParam={selectedParam}
                  onParamChange={onParamChange}
                  favoriteVariables={favoriteVariables}
                  onToggleFavorite={toggleVariableFavorite}
                  loading={loading}
                  expanded={expandedVariableSection}
                  onExpandedChange={onExpandedVariableSectionChange}
                />
              </div>

              {/* Models Section - Allow overflow for color picker */}
              <div className="p-4 weather-models-button" style={{ overflow: 'visible' }}>
                <ModelSection
                  selectedModels={selectedModels}
                  onModelsChange={onModelsChange}
                  availableModels={availableModels}
                  favoriteModels={favoriteModels}
                  onToggleFavorite={toggleModelFavorite}
                  loading={loading}
                  expanded={expandedModelSection}
                  onExpandedChange={onExpandedModelSectionChange}
                  customModelColors={customModelColors}
                  onModelColorChange={onModelColorChange}
                  availableInitTimes={availableInitTimes}
                  loadingInitTimes={loadingInitTimes}
                  openInitTimeSelectors={openInitTimeSelectors}
                  selectedInitTimes={selectedInitTimes}
                  onInitTimeChange={onInitTimeChange}
                  onInitTimeSelectorToggle={onInitTimeSelectorToggle}
                />
              </div>

              {/* Time Range Section */}
              <div className="p-4 time-range-section-button">
                <TimeRangeSection
                  endHour={endHour}
                  onEndHourChange={onEndHourChange}
                  loading={loading}
                  expanded={expandedTimeRangeSection}
                  onExpandedChange={onExpandedTimeRangeSectionChange}
                />
              </div>
            </div>
          </div>

          {/* Custom Coordinate Input Modal - Covers entire card */}
          {showCustomCoordinates && (
            <CustomCoordinateInput
              onSubmit={(customLocation) => {
                setPendingCustomLocation(customLocation);
                setShowCustomCoordinates(false);
              }}
              onCancel={() => setShowCustomCoordinates(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
